import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const LOGO = 'https://storage.readdy-site.link/project_files/0f27b860-7899-4d17-a451-e043696e780c/51ae8833-6174-4697-a4e4-f9113fa91181_cargonet_PNG.png?v=3ebb7523aaf49333dc87e7813a9a53fc';
const FORM_URL = 'https://readdy.ai/api/form/d78d9f1rcamcd36db11g';

type FormData = {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  city: string;
  service: string;
  kvkk: boolean;
};

export default function UyeOlPage() {
  const { isDark } = useTheme();
  const [vis, setVis] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormData>({
    fullName: '', email: '', phone: '', company: '',
    address: '', city: '', service: '', kvkk: false,
  });

  useEffect(() => {
    document.title = 'Üye Ol | CargoNet';
    window.scrollTo(0, 0);
    const t = setTimeout(() => setVis(true), 100);
    return () => clearTimeout(t);
  }, []);

  const set = (k: keyof FormData, v: string | boolean) =>
    setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.phone) {
      setError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    if (!form.kvkk) {
      setError('KVKK metnini onaylamanız gerekmektedir.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          company: form.company,
          address: form.address,
          city: form.city,
          service: form.service,
        }).toString(),
      });
      setSent(true);
    } catch {
      setError('Bir hata oluştu, lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: 'var(--text3)', letterSpacing: '0.07em',
    textTransform: 'uppercase', marginBottom: 8,
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: 'var(--bg)', fontFamily: 'var(--font-b)', paddingTop: 120 }}
    >
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div style={{
          position: 'absolute', top: '-20%', right: '-10%',
          width: 700, height: 700,
          background: 'radial-gradient(circle, rgba(232,97,26,0.13) 0%, transparent 60%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', bottom: '-15%', left: '-8%',
          width: 500, height: 500,
          background: 'radial-gradient(circle, rgba(232,97,26,0.07) 0%, transparent 60%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }} />
      </div>

      {/* Main card */}
      <div
        className="relative w-full"
        style={{
          maxWidth: 1040,
          borderRadius: 28,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)',
          border: '1px solid rgba(255,255,255,0.08)',
          opacity: vis ? 1 : 0,
          transform: vis ? 'translateY(0) scale(1)' : 'translateY(28px) scale(0.96)',
          transition: 'all 0.65s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* ── LEFT PANEL — Hero Image ── */}
        <div className="hidden md:flex flex-col justify-between relative overflow-hidden" style={{ minHeight: 680 }}>
          <img
            src="https://readdy.ai/api/search-image?query=massive%20cargo%20ship%20sailing%20through%20golden%20sunset%20ocean%20waters%2C%20dramatic%20warm%20orange%20sky%20reflecting%20on%20sea%20surface%2C%20aerial%20perspective%2C%20cinematic%20wide%20angle%20photography%2C%20rich%20amber%20and%20deep%20orange%20color%20palette%2C%20professional%20maritime%20logistics%20photography%2C%20moody%20atmospheric%20lighting%20with%20sun%20rays%20breaking%20through%20clouds%2C%20high%20contrast%20dramatic%20scene&width=480&height=680&seq=register-hero-v1&orientation=portrait"
            alt="CargoNet Üyelik"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          {/* Overlays */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(180deg, rgba(6,9,18,0.92) 0%, rgba(6,9,18,0.50) 35%, rgba(6,9,18,0.60) 65%, rgba(6,9,18,0.97) 100%)',
          }} />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(90deg, rgba(232,97,26,0.18) 0%, transparent 60%)',
          }} />

          {/* Logo */}
          <div className="relative z-10 p-10">
            <Link to="/" className="inline-block cursor-pointer">
              <img src={LOGO} alt="CargoNet" className="brightness-0 invert" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
            </Link>
          </div>

          {/* Center content */}
          <div className="relative z-10 px-10 flex-1 flex flex-col justify-center">
            <div className="label mb-5" style={{ display: 'inline-flex' }}>
              <div className="label-dot" />
              Hemen Başlayın
            </div>
            <h2 style={{
              fontFamily: 'var(--font-h)', fontWeight: 800,
              fontSize: 'clamp(30px, 3vw, 42px)',
              color: '#fff', lineHeight: 1.05, marginBottom: 16,
              textShadow: '0 2px 20px rgba(0,0,0,0.8)',
            }}>
              Dünyaya Açılan<br />
              <span style={{
                background: 'linear-gradient(135deg, #E8611A 0%, #FF9A55 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>Kapınız Burada.</span>
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.75, maxWidth: 300, textShadow: '0 1px 12px rgba(0,0,0,0.9)' }}>
              Ücretsiz üye olun, 200+ ülkeye kargo gönderin, lojistik süreçlerinizi tek panelden yönetin.
            </p>

            {/* Feature list */}
            <div className="mt-8 space-y-3">
              {[
                { icon: 'ri-global-line', text: '200+ Ülkeye Teslimat' },
                { icon: 'ri-radar-line', text: 'Anlık Kargo Takibi' },
                { icon: 'ri-price-tag-3-line', text: 'Anında Online Teklif' },
                { icon: 'ri-headphone-line', text: '7/24 Türkçe Destek' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: 'rgba(232,97,26,0.2)', border: '1px solid rgba(232,97,26,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className={f.icon} style={{ color: '#E8611A', fontSize: 13 }} />
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 500, textShadow: '0 1px 8px rgba(0,0,0,0.8)' }}>{f.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom stats */}
          <div className="relative z-10 flex gap-8 px-10 pb-10 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {[
              { n: '200+', l: 'Ülke' },
              { n: '8.4K+', l: 'Aylık Gönderi' },
              { n: '4600+', l: 'Fulfillment' },
            ].map((s) => (
              <div key={s.n}>
                <div style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 20, color: '#fff', lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL — Form ── */}
        <div
          className="flex flex-col justify-center p-8 md:p-10"
          style={{ background: 'var(--bg2)', borderLeft: '1px solid rgba(255,255,255,0.06)' }}
        >
          {/* Mobile logo */}
          <div className="md:hidden mb-6 flex justify-center">
            <Link to="/" className="cursor-pointer">
              <img src={LOGO} alt="CargoNet" className={`h-8 w-auto object-contain ${isDark ? 'brightness-0 invert' : ''}`} />
            </Link>
          </div>

          {sent ? (
            /* Success state */
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-5" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <i className="ri-check-double-line text-green-400 text-2xl" />
              </div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 26, color: 'var(--text1)', marginBottom: 10 }}>
                Başvurunuz Alındı!
              </h2>
              <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 28 }}>
                Ekibimiz en kısa sürede sizinle iletişime geçecek ve hesabınızı aktif edecektir.
              </p>
              <Link to="/" className="btn-primary cursor-pointer" style={{ borderRadius: 12 }}>
                Ana Sayfaya Dön <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ) : (
            <>
              {/* Heading */}
              <div className="mb-6">
                <h1 style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 28, color: 'var(--text1)', marginBottom: 6, lineHeight: 1.1 }}>
                  Üye Ol
                </h1>
                <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>
                  Bilgilerinizi doldurun, ekibimiz sizi arasın.
                </p>
              </div>

              <form data-readdy-form id="uye-ol-form" onSubmit={handleSubmit} className="space-y-4">
                {/* Ad Soyad + Şirket */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label style={labelStyle}>Ad Soyad *</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-3-line" style={{ color: 'var(--text3)', fontSize: 14 }} />
                      </div>
                      <input
                        type="text" name="fullName" placeholder="Ad Soyad" required
                        value={form.fullName} onChange={(e) => set('fullName', e.target.value)}
                        className="inp" style={{ paddingLeft: 38 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Şirket Adı</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-building-2-line" style={{ color: 'var(--text3)', fontSize: 14 }} />
                      </div>
                      <input
                        type="text" name="company" placeholder="Şirket adı (opsiyonel)"
                        value={form.company} onChange={(e) => set('company', e.target.value)}
                        className="inp" style={{ paddingLeft: 38 }}
                      />
                    </div>
                  </div>
                </div>

                {/* E-posta + Telefon */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label style={labelStyle}>E-posta *</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-mail-line" style={{ color: 'var(--text3)', fontSize: 14 }} />
                      </div>
                      <input
                        type="email" name="email" placeholder="ornek@sirket.com" required
                        value={form.email} onChange={(e) => set('email', e.target.value)}
                        className="inp" style={{ paddingLeft: 38 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Telefon *</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-phone-line" style={{ color: 'var(--text3)', fontSize: 14 }} />
                      </div>
                      <input
                        type="tel" name="phone" placeholder="0555 000 00 00" required
                        value={form.phone} onChange={(e) => set('phone', e.target.value)}
                        className="inp" style={{ paddingLeft: 38 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Şehir + Hizmet */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label style={labelStyle}>Şehir</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-2-line" style={{ color: 'var(--text3)', fontSize: 14 }} />
                      </div>
                      <input
                        type="text" name="city" placeholder="İstanbul"
                        value={form.city} onChange={(e) => set('city', e.target.value)}
                        className="inp" style={{ paddingLeft: 38 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>İlgilendiğiniz Hizmet</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-service-line" style={{ color: 'var(--text3)', fontSize: 14 }} />
                      </div>
                      <select
                        name="service"
                        value={form.service} onChange={(e) => set('service', e.target.value)}
                        className="inp" style={{ paddingLeft: 38, cursor: 'pointer' }}
                      >
                        <option value="">Seçiniz</option>
                        <option>Ekspres Kurye</option>
                        <option>E-Ticaret Lojistiği</option>
                        <option>Kargo Taşımacılığı</option>
                        <option>Fulfillment</option>
                        <option>Online Mağaza</option>
                        <option>Mikro İhracat – ETGB</option>
                        <option>A.TR Belgesi</option>
                        <option>On Board Kurye</option>
                        <option>Sigorta</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Adres */}
                <div>
                  <label style={labelStyle}>Adres</label>
                  <textarea
                    name="address" placeholder="Açık adresiniz..."
                    value={form.address}
                    onChange={(e) => { if (e.target.value.length <= 500) set('address', e.target.value); }}
                    rows={3} className="inp" style={{ resize: 'none' }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', marginTop: 3 }}>{form.address.length}/500</div>
                </div>

                {/* KVKK */}
                <div className="flex items-start gap-3">
                  <button
                    type="button" onClick={() => set('kvkk', !form.kvkk)}
                    className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-all duration-200 mt-0.5"
                    style={{ background: form.kvkk ? 'var(--orange)' : 'var(--surface)', border: `1.5px solid ${form.kvkk ? 'var(--orange)' : 'var(--border2)'}` }}
                  >
                    {form.kvkk && <i className="ri-check-line text-white" style={{ fontSize: 11 }} />}
                  </button>
                  <span
                    className="cursor-pointer select-none"
                    style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}
                    onClick={() => set('kvkk', !form.kvkk)}
                  >
                    <strong style={{ color: 'var(--orange)' }}>KVKK Aydınlatma Metni</strong>&apos;ni okudum ve kişisel verilerimin işlenmesine onay veriyorum.
                  </span>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.22)' }}>
                    <i className="ri-error-warning-line" style={{ color: '#f87171', fontSize: 15 }} />
                    <span style={{ fontSize: 13, color: '#f87171', fontWeight: 500 }}>{error}</span>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit" disabled={loading}
                  className="btn-primary w-full justify-center cursor-pointer"
                  style={{ borderRadius: 12, fontSize: 15, opacity: loading ? 0.8 : 1 }}
                >
                  {loading ? (
                    <><i className="ri-loader-4-line" style={{ animation: 'spin 0.8s linear infinite' }} /> Gönderiliyor...</>
                  ) : (
                    <>Üyelik Başvurusu Gönder <i className="ri-arrow-right-line bounce-x" /></>
                  )}
                </button>
              </form>

              {/* Login link */}
              <div className="text-center mt-5">
                <span style={{ fontSize: 14, color: 'var(--text2)' }}>Zaten hesabınız var mı? </span>
                <Link to="/giris" className="cursor-pointer font-bold" style={{ fontSize: 14, color: 'var(--orange)', textDecoration: 'none' }}>
                  Giriş Yap
                </Link>
              </div>

              {/* Back */}
              <div className="text-center mt-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 cursor-pointer"
                  style={{ fontSize: 13, color: 'var(--text3)', textDecoration: 'none', fontWeight: 500 }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text3)')}
                >
                  <i className="ri-arrow-left-line" style={{ fontSize: 13 }} />
                  Ana Sayfaya Dön
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
